import { useState, useCallback } from 'react'
import type { Block, Variable, DemoContent, DemoBackground, DemoTheme } from './types'
import { publishDemoWithCategories } from './api'
import { generateId, BLOCK_DEFAULTS } from './defaults'
import { useAutoSave } from './useAutoSave'
import { useUndoRedo } from './useUndoRedo'
import { evaluateFormula } from '../lib/formula'
import { useFormulaEvaluation } from '../lib/useFormulaEvaluation'

// Re-export for backward compatibility
export { TEMPLATES } from './defaults'
export type { BlockTemplate } from './defaults'

export function useEditorStore(demoId: string, initialContent: DemoContent, initialSlug = '', initialPublished = false) {
  const [blocks, setBlocks] = useState<Block[]>(initialContent.blocks)
  const [variables, setVariables] = useState<Variable[]>(initialContent.variables)
  const [slug] = useState<string>(initialSlug)
  const [published, setPublished] = useState<boolean>(initialPublished)
  const [publishing, setPublishing] = useState(false)
  const [background, setBackgroundState] = useState<DemoBackground | undefined>(initialContent.background)
  const [theme, setThemeState] = useState<DemoTheme | undefined>(initialContent.theme)
  const [variableValues, setVariableValues] = useState<Record<string, number | string | boolean>>(() => {
    const vals: Record<string, number | string | boolean> = {}
    initialContent.variables.forEach((v) => {
      vals[v.name] = v.default
    })
    return vals
  })
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null)

  const { saving, scheduleAutoSave } = useAutoSave(demoId)
  const { canUndo, canRedo, pushHistory, popUndo, popRedo } = useUndoRedo()

  const selectedBlock = blocks.find((b) => b.id === selectedBlockId) ?? null

  const currentSnap = () => ({ blocks, variables, background, theme })

  const restoreSnapshot = useCallback(
    (snap: { blocks: Block[]; variables: Variable[]; background?: DemoBackground; theme?: DemoTheme }) => {
      setBlocks(snap.blocks)
      setVariables(snap.variables)
      setBackgroundState(snap.background)
      setThemeState(snap.theme)
      setVariableValues((prevVals) => {
        const next = { ...prevVals }
        snap.variables.forEach((v) => {
          if (!(v.name in next)) next[v.name] = v.default
        })
        return next
      })
      scheduleAutoSave(snap.blocks, snap.variables, snap.background, snap.theme)
    },
    [scheduleAutoSave]
  )

  const undo = useCallback(() => {
    const prev = popUndo(currentSnap())
    if (prev) restoreSnapshot(prev)
  }, [blocks, variables, background, theme, popUndo, restoreSnapshot])

  const redo = useCallback(() => {
    const next = popRedo(currentSnap())
    if (next) restoreSnapshot(next)
  }, [blocks, variables, background, theme, popRedo, restoreSnapshot])

  const addBlock = useCallback(
    (type: Block['type']) => {
      pushHistory(currentSnap())
      const id = generateId()
      const block: Block = { id, type }

      // Block types that create a variable
      const varBlockConfig: Record<string, { prefix: string; varType: Variable['type']; defaultVal: number | string | boolean }> = {
        slider: { prefix: 'variable', varType: 'slider', defaultVal: 50 },
        steps: { prefix: 'progress', varType: 'steps', defaultVal: 0 },
        toggle: { prefix: 'toggle', varType: 'toggle', defaultVal: false },
        dropdown: { prefix: 'dropdown', varType: 'dropdown', defaultVal: 'option_1' },
        tabs: { prefix: 'tabs', varType: 'tabs', defaultVal: 'tab_1' },
        number_input: { prefix: 'input', varType: 'input', defaultVal: 0 },
        poll: { prefix: 'poll', varType: 'slider', defaultVal: 0 },
        quiz: { prefix: 'quiz', varType: 'dropdown', defaultVal: '' },
        button: { prefix: 'click', varType: 'button', defaultVal: false },
      }

      const cfg = varBlockConfig[type]
      if (cfg) {
        const count = variables.filter((v) => v.type === cfg.varType).length
        const varName = `${cfg.prefix}_${count + 1}`
        block.variable = varName
        if (type === 'slider') {
          block.label = 'New Slider'
        }
        const defaults = BLOCK_DEFAULTS[type]
        if (defaults) Object.assign(block, defaults())

        const newVar: Variable = {
          name: varName, type: cfg.varType, default: cfg.defaultVal,
          ...(cfg.varType === 'slider' ? { min: 0, max: 100, step: 1 } : {}),
        }
        const newVars = [...variables, newVar]
        setVariables(newVars)
        setVariableValues((prev) => ({ ...prev, [varName]: cfg.defaultVal }))
        const newBlocks = [...blocks, block]
        setBlocks(newBlocks)
        setSelectedBlockId(id)
        scheduleAutoSave(newBlocks, newVars, background, theme)
        return
      }

      const defaults = BLOCK_DEFAULTS[type]
      if (defaults) Object.assign(block, defaults())
      if (type === 'chart') block.source = variables[0]?.name ?? ''

      const newBlocks = [...blocks, block]
      setBlocks(newBlocks)
      setSelectedBlockId(id)
      scheduleAutoSave(newBlocks, variables, background, theme)
    },
    [blocks, variables, background, theme, scheduleAutoSave, pushHistory]
  )

  const addTemplate = useCallback(
    (template: { create: (vars: Variable[]) => { blocks: Block[]; variables: Variable[] } }) => {
      pushHistory(currentSnap())
      const result = template.create(variables)
      const newVars = result.variables.length > 0 ? result.variables : variables
      const newBlocks = [...blocks, ...result.blocks]
      setVariables(newVars)
      setBlocks(newBlocks)
      setVariableValues((prev) => {
        const next = { ...prev }
        newVars.forEach((v) => {
          if (!(v.name in next)) next[v.name] = v.default
        })
        return next
      })
      if (result.blocks.length > 0) setSelectedBlockId(result.blocks[0].id)
      scheduleAutoSave(newBlocks, newVars, background, theme)
    },
    [blocks, variables, background, theme, scheduleAutoSave, pushHistory]
  )

  const updateBlock = useCallback(
    (id: string, updates: Partial<Block>) => {
      const newBlocks = blocks.map((b) => (b.id === id ? { ...b, ...updates } : b))
      setBlocks(newBlocks)
      scheduleAutoSave(newBlocks, variables, background, theme)
    },
    [blocks, variables, background, theme, scheduleAutoSave]
  )

  const removeBlock = useCallback(
    (id: string) => {
      pushHistory(currentSnap())
      const block = blocks.find((b) => b.id === id)
      let newVars = variables
      if (block?.variable && (block.type === 'slider' || block.type === 'steps' || block.type === 'toggle' || block.type === 'dropdown' || block.type === 'tabs' || block.type === 'number_input' || block.type === 'poll' || block.type === 'quiz' || block.type === 'button')) {
        newVars = variables.filter((v) => v.name !== block.variable)
        setVariables(newVars)
        setVariableValues((prev) => {
          const next = { ...prev }
          delete next[block.variable!]
          return next
        })
      }
      let newBlocks = blocks.filter((b) => b.id !== id)
      if (block?.type !== 'steps') {
        newBlocks = newBlocks.map((b) => {
          if (b.type === 'steps' && b.config) {
            const cfg = b.config as Record<string, unknown>
            const groups = cfg.stepGroups as Record<string, string[]> | undefined
            if (groups) {
              const cleaned: Record<string, string[]> = {}
              for (const [key, ids] of Object.entries(groups)) {
                cleaned[key] = ids.filter((bid) => bid !== id)
              }
              return { ...b, config: { ...cfg, stepGroups: cleaned } }
            }
          }
          return b
        })
      }
      setBlocks(newBlocks)
      if (selectedBlockId === id) setSelectedBlockId(null)
      scheduleAutoSave(newBlocks, newVars, background, theme)
    },
    [blocks, variables, selectedBlockId, background, theme, scheduleAutoSave, pushHistory]
  )

  const duplicateBlock = useCallback(
    (id: string) => {
      pushHistory(currentSnap())
      const block = blocks.find((b) => b.id === id)
      if (!block) return

      const newId = generateId()
      const clone: Block = { ...block, id: newId, config: block.config ? { ...block.config } : undefined }
      if (block.visibility) {
        clone.visibility = { ...block.visibility, conditions: block.visibility.conditions.map((c) => ({ ...c })) }
      }

      const varBlockConfig: Record<string, { prefix: string; varType: Variable['type']; defaultVal: number | string | boolean; resetConfig?: Record<string, unknown> }> = {
        slider: { prefix: 'variable', varType: 'slider', defaultVal: 50 },
        steps: { prefix: 'progress', varType: 'steps', defaultVal: 0, resetConfig: { stepGroups: {} } },
        toggle: { prefix: 'toggle', varType: 'toggle', defaultVal: false },
        dropdown: { prefix: 'dropdown', varType: 'dropdown', defaultVal: 'option_1' },
        tabs: { prefix: 'tabs', varType: 'tabs', defaultVal: 'tab_1' },
        number_input: { prefix: 'input', varType: 'input', defaultVal: 0 },
        quiz: { prefix: 'quiz', varType: 'dropdown', defaultVal: '' },
      }

      const cfg = varBlockConfig[block.type]
      if (cfg && block.variable) {
        const count = variables.filter((v) => v.type === cfg.varType).length
        const varName = `${cfg.prefix}_${count + 1}`
        clone.variable = varName
        if (cfg.resetConfig) {
          clone.config = { ...(clone.config as Record<string, unknown>), ...cfg.resetConfig }
        }
        const origVar = variables.find((v) => v.name === block.variable)
        const newVar: Variable = origVar
          ? { ...origVar, name: varName }
          : { name: varName, type: cfg.varType, default: cfg.defaultVal }
        const newVars = [...variables, newVar]
        setVariables(newVars)
        setVariableValues((prev) => ({ ...prev, [varName]: origVar?.default ?? cfg.defaultVal }))
        const idx = blocks.findIndex((b) => b.id === id)
        const newBlocks = [...blocks]
        newBlocks.splice(idx + 1, 0, clone)
        setBlocks(newBlocks)
        setSelectedBlockId(newId)
        scheduleAutoSave(newBlocks, newVars, background, theme)
        return
      }

      const idx = blocks.findIndex((b) => b.id === id)
      const newBlocks = [...blocks]
      newBlocks.splice(idx + 1, 0, clone)
      setBlocks(newBlocks)
      setSelectedBlockId(newId)
      scheduleAutoSave(newBlocks, variables, background, theme)
    },
    [blocks, variables, background, theme, scheduleAutoSave, pushHistory]
  )

  const resetBlock = useCallback(
    (id: string) => {
      pushHistory(currentSnap())
      const block = blocks.find((b) => b.id === id)
      if (!block) return

      const defaults = BLOCK_DEFAULTS[block.type]
      const resetData: Partial<Block> = defaults ? defaults() : {}

      if (block.type === 'slider' && block.variable) {
        resetData.label = 'New Slider'
        const newVars = variables.map((v) =>
          v.name === block.variable ? { ...v, default: 50, min: 0, max: 100, step: 1 } : v
        )
        setVariables(newVars)
        setVariableValues((prev) => ({ ...prev, [block.variable!]: 50 }))
        const newBlocks = blocks.map((b) => (b.id === id ? { ...b, ...resetData, config: {}, visibility: undefined } : b))
        setBlocks(newBlocks)
        scheduleAutoSave(newBlocks, newVars, background, theme)
        return
      }

      const newBlocks = blocks.map((b) => (b.id === id ? { ...b, ...resetData, animation: undefined, visibility: undefined } : b))
      setBlocks(newBlocks)
      scheduleAutoSave(newBlocks, variables, background, theme)
    },
    [blocks, variables, background, theme, scheduleAutoSave, pushHistory]
  )

  const moveBlock = useCallback(
    (fromIndex: number, toIndex: number) => {
      pushHistory(currentSnap())
      const newBlocks = [...blocks]
      const [moved] = newBlocks.splice(fromIndex, 1)
      newBlocks.splice(toIndex, 0, moved)
      setBlocks(newBlocks)
      scheduleAutoSave(newBlocks, variables, background, theme)
    },
    [blocks, variables, background, theme, scheduleAutoSave, pushHistory]
  )

  const updateVariable = useCallback(
    (name: string, updates: Partial<Variable>) => {
      const newVars = variables.map((v) => (v.name === name ? { ...v, ...updates } : v))
      setVariables(newVars)
      scheduleAutoSave(blocks, newVars, background, theme)
    },
    [blocks, variables, background, theme, scheduleAutoSave]
  )

  const renameVariable = useCallback(
    (oldName: string, newName: string) => {
      if (oldName === newName || variables.some((v) => v.name === newName)) return

      const newVars = variables.map((v) => {
        const updated = v.name === oldName ? { ...v, name: newName } : { ...v }
        if (updated.formula) {
          updated.formula = updated.formula.replace(new RegExp(`@${oldName}\\b`, 'g'), `@${newName}`)
        }
        return updated
      })
      setVariables(newVars)

      const newBlocks = blocks.map((b) => {
        const updated = { ...b }
        if (updated.variable === oldName) updated.variable = newName
        if (updated.source === oldName) updated.source = newName
        if (updated.content) {
          updated.content = updated.content.replace(new RegExp(`@${oldName}\\b`, 'g'), `@${newName}`)
        }
        if (updated.visibility) {
          updated.visibility = {
            ...updated.visibility,
            conditions: updated.visibility.conditions.map((c) =>
              c.variable === oldName ? { ...c, variable: newName } : c
            ),
          }
        }
        return updated
      })
      setBlocks(newBlocks)

      setVariableValues((prev) => {
        const next = { ...prev }
        next[newName] = next[oldName]
        delete next[oldName]
        return next
      })

      scheduleAutoSave(newBlocks, newVars, background, theme)
    },
    [blocks, variables, background, theme, scheduleAutoSave]
  )

  const addVariable = useCallback(
    (name?: string): string => {
      pushHistory(currentSnap())
      const varName = name || `variable_${variables.length + 1}`
      if (variables.some((v) => v.name === varName)) return varName
      const newVar: Variable = { name: varName, type: 'slider', default: 0, min: 0, max: 100, step: 1 }
      const newVars = [...variables, newVar]
      setVariables(newVars)
      setVariableValues((prev) => ({ ...prev, [varName]: 0 }))
      scheduleAutoSave(blocks, newVars, background, theme)
      return varName
    },
    [blocks, variables, background, theme, scheduleAutoSave, pushHistory]
  )

  const setVariableValue = useCallback((name: string, value: number | string | boolean) => {
    setVariableValues((prev) => ({ ...prev, [name]: value }))
  }, [])

  // Evaluate formula variables reactively
  useFormulaEvaluation(variables, variableValues, setVariableValue)

  const addFormulaVariable = useCallback(
    (name: string, formula: string) => {
      if (variables.some((v) => v.name === name)) return
      pushHistory(currentSnap())
      const newVar: Variable = { name, type: 'formula', default: 0, formula }
      const newVars = [...variables, newVar]
      setVariables(newVars)
      const result = evaluateFormula(formula, variableValues)
      setVariableValues((prev) => ({ ...prev, [name]: Math.round(result * 1e6) / 1e6 }))
      scheduleAutoSave(blocks, newVars, background, theme)
    },
    [blocks, variables, variableValues, background, theme, scheduleAutoSave, pushHistory]
  )

  const updateFormulaVariable = useCallback(
    (name: string, formula: string) => {
      pushHistory(currentSnap())
      const newVars = variables.map((v) => v.name === name ? { ...v, formula } : v)
      setVariables(newVars)
      const result = evaluateFormula(formula, variableValues)
      setVariableValues((prev) => ({ ...prev, [name]: Math.round(result * 1e6) / 1e6 }))
      scheduleAutoSave(blocks, newVars, background, theme)
    },
    [blocks, variables, variableValues, background, theme, scheduleAutoSave, pushHistory]
  )

  const removeFormulaVariable = useCallback(
    (name: string) => {
      pushHistory(currentSnap())
      const frozenValue = variableValues[name]
      const frozenStr = frozenValue !== undefined ? String(frozenValue) : '0'

      const newVars = variables
        .filter((v) => v.name !== name)
        .map((v) => {
          if (v.formula && v.formula.includes(`@${name}`)) {
            return { ...v, formula: v.formula.replace(new RegExp(`@${name}\\b`, 'g'), frozenStr) }
          }
          return v
        })
      setVariables(newVars)

      const newBlocks = blocks.map((b) => {
        let updated = b
        if (updated.content && updated.content.includes(`@${name}`)) {
          updated = { ...updated, content: updated.content.replace(new RegExp(`@${name}\\b`, 'g'), frozenStr) }
        }
        if (updated.source === name) {
          updated = { ...updated, source: '' }
        }
        if (updated.visibility) {
          const remaining = updated.visibility.conditions.filter((c) => c.variable !== name)
          if (remaining.length === 0) {
            updated = { ...updated, visibility: undefined }
          } else if (remaining.length < updated.visibility.conditions.length) {
            updated = { ...updated, visibility: { ...updated.visibility, conditions: remaining } }
          }
        }
        return updated
      })
      setBlocks(newBlocks)

      setVariableValues((prev) => {
        const next = { ...prev }
        delete next[name]
        return next
      })
      scheduleAutoSave(newBlocks, newVars, background, theme)
    },
    [blocks, variables, variableValues, background, theme, scheduleAutoSave, pushHistory]
  )

  const getLinkedBlocks = useCallback(
    (variableName: string) => {
      return blocks.filter(
        (b) => b.source === variableName || (b.content && b.content.includes(`@${variableName}`))
      )
    },
    [blocks]
  )

  const setBackground = useCallback(
    (bg: DemoBackground) => {
      pushHistory(currentSnap())
      setBackgroundState(bg)
      scheduleAutoSave(blocks, variables, bg, theme)
    },
    [blocks, variables, background, theme, scheduleAutoSave, pushHistory]
  )

  const setTheme = useCallback(
    (t: DemoTheme) => {
      pushHistory(currentSnap())
      setThemeState(t)
      scheduleAutoSave(blocks, variables, background, t)
    },
    [blocks, variables, background, scheduleAutoSave, pushHistory]
  )

  const selectNextBlock = useCallback(
    (direction: 1 | -1) => {
      if (blocks.length === 0) return
      if (!selectedBlockId) { setSelectedBlockId(blocks[0].id); return }
      const idx = blocks.findIndex((b) => b.id === selectedBlockId)
      const next = Math.max(0, Math.min(blocks.length - 1, idx + direction))
      setSelectedBlockId(blocks[next].id)
    },
    [blocks, selectedBlockId]
  )

  const publish = useCallback(async (categoryIds: number[] = []) => {
    setPublishing(true)
    try {
      const result = await publishDemoWithCategories(demoId, categoryIds)
      setPublished(true)
      return result
    } catch (e) {
      console.error('Publish failed:', e)
      throw e
    } finally {
      setPublishing(false)
    }
  }, [demoId])

  const getStepVisibility = useCallback((): { controlled: Set<string>; visible: Set<string> } => {
    const controlled = new Set<string>()
    const visible = new Set<string>()
    for (const block of blocks) {
      if (block.type !== 'steps' || !block.config) continue
      const cfg = block.config as Record<string, unknown>
      const groups = cfg.stepGroups as Record<string, string[]> | undefined
      if (!groups) continue
      const currentStep = Number(block.variable ? variableValues[block.variable] ?? 0 : 0)
      for (const [stepIdx, blockIds] of Object.entries(groups)) {
        for (const bid of blockIds) {
          controlled.add(bid)
          if (Number(stepIdx) === currentStep) visible.add(bid)
        }
      }
    }
    return { controlled, visible }
  }, [blocks, variableValues])

  return {
    blocks, variables, variableValues, selectedBlock, selectedBlockId, saving, slug,
    published, publishing, background, theme, canUndo, canRedo,
    publish, undo, redo, setSelectedBlockId,
    addBlock, addTemplate, updateBlock, removeBlock, duplicateBlock, resetBlock, moveBlock,
    updateVariable, renameVariable, setVariableValue, addVariable,
    addFormulaVariable, updateFormulaVariable, removeFormulaVariable,
    getLinkedBlocks, setBackground, setTheme, selectNextBlock, getStepVisibility,
  }
}
